import React from "react";
import { UltravioletCaptureProperty } from "../../Model/CaptureProfile";
import { LightPathCapability } from "../../Model/ImagerCapabilities";
import { Fov, ImagerState } from "../../Model/ModuleState";
import { BinningControl } from "./Control/BinningControl";
import { ExposureControl } from "./Control/ExposureControl";
import FovControl from "./Control/FovControl";
import { GainControl } from "./Control/GainControl";
import { GammaControl } from "./Control/GammaControl";
import { IlluminationControl } from "./Control/IlluminationControl";
import { ImagerInfo } from "./Control/ImagerInfoControl";
import { ObjectiveZoomControl } from "./Control/ObjectiveZoomControl";
import { CropZoomControl } from "./Control/CropZoomControl";

type Props = {
  disabled: boolean;
  lightPathCapability: LightPathCapability;
  ultravioletCaptureProperty: UltravioletCaptureProperty;
  onUltravioletCapturePropertyChanged: (
    ultravioletCaptureProperty: UltravioletCaptureProperty
  ) => void;
  fov: Fov;
  onAutoExposure?: () => void;
  imagerState: ImagerState;
  style?: React.CSSProperties;
};

export default function UltravioleteOpticAndCameraControl({
  disabled,
  lightPathCapability,
  ultravioletCaptureProperty,
  onAutoExposure,
  fov,
  onUltravioletCapturePropertyChanged,
  imagerState,
}: Props) {
  let opticAndCameraControls = [];

  //-------OPTIC------//

  const objectiveZoom = lightPathCapability.optic.objectiveZoom;
  const cropZoom = lightPathCapability.optic.cropZoom;

  //-------Camera------//

  const exposure = lightPathCapability.camera.exposure;
  const gamma = lightPathCapability.camera.gamma;
  const gain = lightPathCapability.camera.gain;
  const binning = lightPathCapability.camera.binning;

  if (objectiveZoom) {
    if (ultravioletCaptureProperty.objectiveZoom == null) {
      throw new Error("Capture profile objective zoom is null");
    }
    opticAndCameraControls.push(
      <ObjectiveZoomControl
        objectiveZoom={objectiveZoom}
        disabled={disabled}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  if (cropZoom) {
    if (ultravioletCaptureProperty.cropZoom == null) {
      throw new Error("Capture profile crop zoom is null");
    }
    opticAndCameraControls.push(
      <CropZoomControl
        cropZoom={cropZoom}
        disabled={disabled}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  let zoom = 1;
  let maxZoom = 1;

  if (ultravioletCaptureProperty.objectiveZoom != null && objectiveZoom != null) {
    zoom = ultravioletCaptureProperty.objectiveZoom;
    maxZoom =
      objectiveZoom.objectives[objectiveZoom.objectives.length - 1].value;
  }

  const maxFov: Fov = {
    width: fov.width * zoom,
    height: fov.height * zoom,
  };

  const minFov: Fov = {
    width: maxFov.width / maxZoom,
    height: maxFov.height / maxZoom,
  };

  opticAndCameraControls.push(
    <FovControl
      id="Fov"
      fov={fov}
      maxFov={maxFov}
      minFov={minFov}
      onValueChanged={(id, fov) => {}}
      disabled={true}
    />
  );

  if (ultravioletCaptureProperty.illumination == null) {
    throw new Error("Capture profile illumination zoom is null");
  }

  opticAndCameraControls.push(
    <IlluminationControl
      disabled={disabled}
      captureProperty={ultravioletCaptureProperty}
      onChange={onUltravioletCapturePropertyChanged}
    />
  );

  if (gamma) {
    opticAndCameraControls.push(
      <GammaControl
        gamma={gamma}
        disabled={disabled}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  if (gain) {
    opticAndCameraControls.push(
      <GainControl
        gain={gain}
        disabled={disabled}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  if (exposure) {
    opticAndCameraControls.push(
      <ExposureControl
        exposure={exposure}
        disabled={disabled}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  if (binning) {
    opticAndCameraControls.push(
      <BinningControl
        disabled={disabled}
        binning={binning}
        captureProperty={ultravioletCaptureProperty}
        onChange={onUltravioletCapturePropertyChanged}
      />
    );
  }

  opticAndCameraControls.push(
    <ImagerInfo
      disabled={disabled}
      imagerState={imagerState}
      captureProperty={ultravioletCaptureProperty}
    />
  );

  return <>{opticAndCameraControls}</>;
}
